import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Header from './Header';

/** * NAVIGATION COMPONENT
 * Utilizes Aqua Haze (#F0F4F8) background with Cerulean Blue (#2B6CB0) accents.
 * Fixed positioning with backdrop blur for a modern, glassmorphism feel.
 */
const Nav = () => (
  <nav className="fixed top-0 w-full z-50 bg-[#F0F4F8]/80 backdrop-blur-xl border-b border-[#2B6CB0]/10 py-5 px-8">
    <div className="max-w-7xl mx-auto flex justify-between items-center">
      <div className="text-[#020617] font-black text-2xl tracking-tighter">
        LOGO<span className="text-[#2B6CB0]">.</span>
      </div>
      <div className="hidden md:flex gap-10">
        {['Work', 'Services', 'About', 'Contact'].map((item) => (
          <a key={item} href={`#${item.toLowerCase()}`} className="text-[#4A5568] hover:text-[#2B6CB0] text-[11px] font-bold uppercase tracking-[0.2em] transition-all">
            {item}
          </a>
        ))}
      </div>
      <button className="bg-[#020617] text-white px-8 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-[#2B6CB0] transition-colors">
        Start Project
      </button>
    </div>
  </nav>
);

/**
 * PROJECT ROW COMPONENT
 * Dynamic grid layout: Left side contains metadata in River Bed (#4A5568),
 * Right side features a grayscale-to-color image carousel with Cerulean Blue overlays.
 */
const ProjectRow = ({ project }) => {
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % project.images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [project.images.length]);

  return (
    <div className="group relative grid grid-cols-1 lg:grid-cols-12 gap-0 mb-24 overflow-hidden rounded-[2.5rem] bg-[#F0F4F8] border-2 border-[#2B6CB0] hover:shadow-2xl transition-all duration-500">
      {/* LEFT SIDE: PROJECT INFO */}
      <div className="lg:col-span-5 p-10 md:p-16 flex flex-col justify-between min-h-[450px]">
        <div>
          <div className="flex items-center gap-4 mb-6">
            <span className="bg-[#2B6CB0] text-white text-[9px] font-black px-3 py-1 rounded-full uppercase tracking-widest">
              {project.category}
            </span>
            <span className="text-[#4A5568]/40 font-mono text-sm">{project.year}</span>
          </div>
          
          <h3 className="text-5xl font-black text-[#020617] mb-6 leading-none tracking-tight uppercase">
            {project.title}
          </h3>
          
          <p className="text-[#4A5568] text-lg mb-10 leading-relaxed max-w-md font-medium">
            {project.description}
          </p>

          <div className="flex flex-wrap gap-2 mb-10">
            {project.services.map((s, i) => (
              <span key={i} className="text-[10px] font-bold text-[#4A5568] border border-[#4A5568]/20 px-3 py-1 rounded-full uppercase">
                {s}
              </span>
            ))}
          </div>
        </div>

<div className="flex items-center gap-4 text-[#4A5568]/50 font-black text-xs uppercase tracking-[0.2em] select-none">
  <span className="w-12 h-12 rounded-full border-2 border-[#4A5568]/10 flex items-center justify-center italic">
    ×
  </span>

  {project.link && (
    <a
      href={project.link}
      target="_blank"
      rel="noopener noreferrer"
      className="px-4 py-2 border border-[#4A5568]/20 rounded-lg hover:bg-[#1f2937] hover:text-white transition duration-300 cursor-pointer"
    >
      View Online
    </a>
  )}
</div>
      </div>

      {/* RIGHT SIDE: VISUALS */}
      <div className="lg:col-span-7 relative h-[400px] lg:h-auto overflow-hidden bg-[#4A5568]/10">
        {project.images.map((img, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
              index === currentImage ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
            }`}
          >
            <img
              src={img}
              alt="Project TBA Preview"
              className="w-full h-full object-cover transition-all duration-700"
            />
          </div>
        ))}

        <div className="absolute top-10 right-10 bg-[#020617] text-white px-4 py-2 rounded-sm text-[10px] font-black uppercase tracking-[0.3em]">
          {project.tag}
        </div>
      </div>
    </div>
  );
};

/**
 * MAIN WORK SECTION
 * Includes Header with Category Filtering and the Project Feed.
 * All project descriptions and services are placeholder (TBA) as requested.
 */
function Work() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const projects = [
    // WEB DESIGN
    {
      title: "Flame & Fusion",
      year: "2025",
      category: "Web Design",
      tag: "Live",
      link: "https://www.flameandfusionusa.com/",
      description: "Custom restaurant website for a Halal street food brand in Warren, MI. Built with food delivery integrations (DoorDash, GrubHub, Uber Eats), full menu design, and brand identity.",
      services: ["Web Design", "Development", "Branding", "SEO"],
      images: ["/flame1.png", "/flame2.png"]
    },
    {
      title: "Express Hot Chicken",
      year: "2025",
      category: "Web Design",
      tag: "Live",
      link: "https://www.expresshotchicken.caffryx.com/",
      description: "Custom restaurant website for a hot chicken brand. Built with online ordering integration, full menu showcase, and bold brand identity.",
      services: ["Web Design", "Development", "Branding", "SEO"],
      images: ["/express1.png", "/express2.png"]
    },
    // BRANDING
    {
      title: "Project TBA 01",
      year: "2026",
      category: "Branding",
      tag: "In Progress",
      description: "TBA",
      services: ["TBA", "TBA", "TBA"],
      images: ["https://images.unsplash.com/photo-1558655146-d09347e92766?q=80&w=1200", "https://images.unsplash.com/photo-1626785774573-4b799315345d?q=80&w=1200"]
    },
    {
      title: "Project TBA 02",
      year: "2026",
      category: "Branding",
      tag: "Confidential",
      description: "TBA",
      services: ["TBA", "TBA", "TBA"],
      images: ["https://images.unsplash.com/photo-1605648916319-cf082f7524a1?q=80&w=1200", "https://images.unsplash.com/photo-1556228720-195a672e8a03?q=80&w=1200"]
    },
    // SOCIAL MEDIA
    {
      title: "Project TBA 03",
      year: "2026",
      category: "Social Media",
      tag: "In Production",
      description: "TBA",
      services: ["TBA", "TBA", "TBA"],
      images: ["https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=1200", "https://images.unsplash.com/photo-1514525253361-bee871871771?q=80&w=1200"]
    },
    {
      title: "Project TBA 04",
      year: "2026",
      category: "Social Media",
      tag: "V1.0 Launch",
      description: "TBA",
      services: ["TBA", "TBA", "TBA"],
      images: ["https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?q=80&w=1200", "https://images.unsplash.com/photo-1551288049-bbbda546697a?q=80&w=1200"]
    },
    // E-COMMERCE
    {
      title: "Project TBA 07",
      year: "2026",
      category: "E-Commerce",
      tag: "Pre-Launch",
      description: "TBA",
      services: ["TBA", "TBA", "TBA"],
      images: ["https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=1200", "https://images.unsplash.com/photo-1472851294608-062f824d29cc?q=80&w=1200"]
    },
    {
      title: "Project TBA 08",
      year: "2026",
      category: "E-Commerce",
      tag: "Scaling Phase",
      description: "TBA",
      services: ["TBA", "TBA", "TBA"],
      images: ["https://images.unsplash.com/photo-1523381210434-271e8be1f52b?q=80&w=1200", "https://images.unsplash.com/photo-1469033019974-296ff9bd901f?q=80&w=1200"]
    }
  ];

  const categories = ['All', 'Web Design', 'Branding', 'Social Media', 'E-Commerce'];

  const filteredProjects = activeFilter === 'All'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  // Global mouse tracking for the parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Auto-cycle categories every 5 seconds
  useEffect(() => {
    const categoryTimer = setInterval(() => {
      setCurrentCategoryIndex((prev) => (prev + 1) % categories.length);
      setActiveFilter(categories[(currentCategoryIndex + 1) % categories.length]);
    }, 5000);
    return () => clearInterval(categoryTimer);
  }, [categories, currentCategoryIndex]);

  return (
    <div className="relative overflow-x-hidden bg-[#F0F4F8] text-[#4A5568] antialiased font-sans min-h-screen">
      <Header />

      {/* --- GLOBAL DYNAMIC BACKGROUND (NOW FULL PAGE) --- */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        {/* Grid Overlay */}
        <div className="absolute inset-0 opacity-[0.15]"
             style={{ backgroundImage: `radial-gradient(#2B6CB0 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

        {/* Floating Interactive Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                x: mousePos.x * (i + 1) * 40,
                y: mousePos.y * (i + 1) * 40,
                rotate: mousePos.x * 45,
              }}
              transition={{ type: 'spring', stiffness: 40, damping: 20 }}
              className="absolute border border-[#2B6CB0]/20 rounded-2xl"
              style={{
                width: `${(i + 1) * 250}px`,
                height: `${(i + 1) * 250}px`,
                left: `${20 + i * 20}%`,
                top: `${15 + i * 20}%`,
              }}
            />
          ))}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .marquee-text { animation: marquee 40s linear infinite; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />

      <Nav />
      
      {/* HEADER SECTION: Displays big bold typography and category filters. */}
      <header className="relative z-10 pt-40 pb-20 px-8 bg-transparent">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-[#020617] text-7xl md:text-[9rem] font-black tracking-tighter leading-[0.85] mb-12 uppercase">
            SELECTED<br /> <span className="text-[#2B6CB0]">WORKS.</span>
          </h1>
          <div className="flex flex-col md:flex-row justify-between items-center gap-8 border-t border-[#F0F4F8] pt-12">
            <p className="text-[#4A5568] max-w-sm text-lg font-medium">
              A curated selection of upcoming releases and strategic creative partnerships.
            </p>
            <div className="flex items-center gap-4 bg-[#F0F4F8] p-1.5 rounded-full border-2 border-[#2B6CB0]">
              <button
                onClick={() => {
                  const newIndex = (currentCategoryIndex - 1 + categories.length) % categories.length;
                  setCurrentCategoryIndex(newIndex);
                  setActiveFilter(categories[newIndex]);
                }}
                className="p-2 text-[#4A5568] hover:text-[#2B6CB0] transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              <div
                className={`px-6 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all
                  ${activeFilter === categories[currentCategoryIndex] ? 'bg-[#2B6CB0] text-white shadow-md' : 'text-[#4A5568] bg-white'}`}
              >
                {categories[currentCategoryIndex]}
              </div>
              <button
                onClick={() => {
                  const newIndex = (currentCategoryIndex + 1) % categories.length;
                  setCurrentCategoryIndex(newIndex);
                  setActiveFilter(categories[newIndex]);
                }}
                className="p-2 text-[#4A5568] hover:text-[#2B6CB0] transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* PROJECT FEED: Iterates through the filtered project list. */}
      <section className="relative z-10 px-8 pb-32">
        <div className="max-w-7xl mx-auto space-y-12">
          {filteredProjects.map((p, idx) => (
            <ProjectRow key={idx} project={p} />
          ))}
        </div>
      </section>

      {/* CALL TO ACTION: High-contrast footer with a Cerulean Blue primary button. */}
<section className="bg-[#020617] py-24 px-8 w-full">
  <div className="max-w-4xl mx-auto text-center">
    {/* Accent text using Cerulean Blue */}
    <span className="text-[#2B6CB0] font-black uppercase tracking-[0.4em] text-xs mb-6 block">
      Inquiry
    </span>

    {/* Heading using Aqua Haze for the italic accent */}
    <h2 className="text-white text-4xl md:text-7xl font-black tracking-tighter mb-10 leading-tight">
      Scale your <span className="italic font-light text-[#F0F4F8]">Digital</span> footprint.
    </h2>

    {/* Primary CTA Button */}
    <button className="bg-[#2B6CB0] text-white px-12 py-5 rounded-full font-black uppercase tracking-widest text-xs hover:bg-[#2B6CB0]/90 hover:scale-105 transition-all shadow-xl shadow-[#2B6CB0]/20 mx-auto block">
      Book a Strategy Call
    </button>
  </div>
</section>
    </div>
  );
}

export default Work;